import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Check, X, FileText, Printer, Copy, CheckCheck, ChevronLeft, ChevronRight, ZoomIn, Image, ScrollText, ArrowLeft, Shield } from 'lucide-react';
import { Toaster, toast } from 'sonner';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const brl = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);
const PAGAMENTO_LABELS = { pix: 'PIX', cartao_avista: 'Cartão à Vista', cartao_parcelado: 'Cartão Parcelado', boleto: 'Boleto' };

const DEFAULT_TERMS = `TERMO DE ACEITE DE ORÇAMENTO\nE CONTRATO DE PRESTAÇÃO DE SERVIÇOS\n\n\nCláusula 1ª – Objeto do Contrato\nO presente instrumento tem por objeto a prestação dos serviços descritos no orçamento identificado acima, de acordo com as especificações, quantidades e condições nele constantes, que passa a fazer parte integrante deste contrato independentemente de transcrição.\n\nEm caso de atraso no pagamento, incidirão multa de 2% sobre o valor em aberto, acrescida de juros de 1% ao mês e correção monetária pelo IGPM/FGV.\n\nCláusula 3ª – Prazo de Execução\nOs serviços serão iniciados a partir da data de aprovação deste orçamento e deverão ser concluídos até o prazo que consta no orçamento, podendo o prazo ser prorrogado mediante acordo entre as partes, por meio de mensagens eletrônicas ou pessoalmente, em casos fortuitos ou de força maior, alterações solicitadas pelo Contratante ou atrasos decorrentes do fornecimento de materiais e/ou informações por parte do Contratante.\n\nCláusula 4ª – Garantia dos Serviços\nO Contratado garante os serviços executados pelo prazo definido no orçamento a contar da data de conclusão e entrega, cobrindo vícios ou defeitos de execução, excluindo-se desgaste natural, mau uso, danos causados por terceiros ou eventos externos alheios ao Contratado.\n\nCláusula 5ª – Obrigações das Partes\n5.1 Obrigações do Contratado:\na) Executar os serviços conforme especificado no orçamento, com qualidade e nos prazos acordados;\nb) Manter o Contratante informado sobre o andamento dos serviços;\nc) Responsabilizar-se pelos encargos trabalhistas e previdenciários de seus colaboradores.\n\n5.2 Obrigações do Contratante:\na) Efetuar os pagamentos nas condições e nos prazos acordados;\nb) Fornecer acesso ao local de trabalho e as informações necessárias para a execução dos serviços;\nc) Não contratar terceiros para executar os mesmos serviços durante a vigência deste contrato.\n\nCláusula 6ª – Rescisão e Cancelamento\nO cancelamento do serviço após o início da execução implicará o pagamento proporcional ao trabalho realizado até a data da rescisão, acrescido de multa de 10% (dez por cento) sobre o valor total do contrato, a título de ressarcimento por gastos e reserva de agenda.\n\nCláusula 7ª – Disposições Gerais\nEste instrumento é celebrado com base no artigo 425 do Código Civil Brasileiro (Lei nº 10.406/2002), que autoriza as partes a estipular contratos atípicos. Para fins de validade, a assinatura física ou eletrônica (nos termos da Lei nº 14.063/2020 e MP 2.200-2/2001) tem plena eficácia jurídica. O foro eleito para dirimir eventuais conflitos é o da Comarca de [Cidade – UF], com renúncia a qualquer outro, por mais privilegiado que seja.\n\n\nDeclaro que li, entendi e concordo com todos os termos e condições acima.`;

export default function PublicProposal() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [proposal, setProposal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [pixCopied, setPixCopied] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTermsOverlay, setShowTermsOverlay] = useState(false);
  const [termsError, setTermsError] = useState(false);
  const [docScale, setDocScale] = useState(1);
  const isLoggedIn = !!localStorage.getItem('orcfacil_token');

  useEffect(() => {
    const update = () => setDocScale(Math.min((window.innerWidth - 16) / 794, 1));
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const photos = proposal?.execution_photos || [];

  const openLightbox = (idx) => setLightboxIndex(idx);
  const closeLightbox = () => setLightboxIndex(null);
  const prevPhoto = useCallback(() => setLightboxIndex(i => (i > 0 ? i - 1 : photos.length - 1)), [photos.length]);
  const nextPhoto = useCallback(() => setLightboxIndex(i => (i < photos.length - 1 ? i + 1 : 0)), [photos.length]);

  useEffect(() => {
    if (lightboxIndex === null) return;
    const handler = (e) => {
      if (e.key === 'ArrowLeft') prevPhoto();
      else if (e.key === 'ArrowRight') nextPhoto();
      else if (e.key === 'Escape') closeLightbox();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightboxIndex, prevPhoto, nextPhoto]);

  const copyPix = (chave) => {
    navigator.clipboard.writeText(chave).then(() => {
      setPixCopied(true);
      toast.success('Chave PIX copiada!');
      setTimeout(() => setPixCopied(false), 3000);
    }).catch(() => toast.error('Não foi possível copiar'));
  };

  useEffect(() => {
    axios.get(`${BACKEND}/api/proposals/public/${id}`)
      .then(r => setProposal(r.data))
      .catch(() => setProposal(null))
      .finally(() => setLoading(false));
  }, [id]);

  const act = async (action) => {
    if (action === 'approve' && !termsAccepted) {
      setTermsError(true);
      toast.error('É preciso ler e aceitar o Termo e Condições.');
      return;
    }
    setTermsError(false);
    if (action === 'approve') setApproving(true); else setRejecting(true);
    try {
      await axios.post(`${BACKEND}/api/proposals/public/${id}/action`, { action });
      setProposal(p => ({ ...p, status: action === 'approve' ? 'aprovado' : 'recusado' }));
      toast.success(action === 'approve' ? 'Orçamento aprovado! Obrigado.' : 'Orçamento recusado.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao processar');
    } finally { setApproving(false); setRejecting(false); }
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-200 flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!proposal) return (
    <div className="min-h-screen bg-gray-200 flex items-center justify-center p-4">
      <div className="text-center space-y-3">
        <FileText size={40} className="text-gray-400 mx-auto" />
        <h2 className="font-bold text-xl text-gray-800">Orçamento não encontrado</h2>
        <p className="text-gray-500 text-sm">Este link pode ter expirado ou sido removido.</p>
      </div>
    </div>
  );

  const empresa = proposal.empresa_dados || {};
  const allItems = [...(proposal.itens || [])].sort((a, b) => a.position - b.position);
  const isService = (i) => i.secao === 'servico' || i.tipo === 'servico';
  const serviceItems = allItems.filter(isService);
  const materialItems = allItems.filter(i => !isService(i));
  const totalServicos = serviceItems.reduce((s, i) => s + (i.valor_total || 0), 0);
  const totalMateriais = materialItems.reduce((s, i) => s + (i.valor_total || 0), 0);
  const subtotal = totalServicos + totalMateriais;
  const fp = proposal.forma_pagamento;
  const canAct = ['nao_enviado', 'enviado'].includes(proposal.status);

  const cidadeUF = [empresa.cidade, empresa.uf].filter(Boolean).join(' – ') || 'sua cidade';
  const termsText = (empresa.terms_conditions || DEFAULT_TERMS)
    .replace(/\[Cidade\s*[–-]\s*UF\]/g, cidadeUF);

  const openPrintPage = () => {
    const win = window.open('', '_blank');
    if (!win) return;
    const html = `<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8"/>
  <meta name="viewport" content="width=device-width, initial-scale=1.0"/>
  <title>Orçamento #${proposal.num} — ${empresa.nome || ''}</title>
  <style>
    *{box-sizing:border-box;margin:0;padding:0}
    body{font-family:Arial,Helvetica,sans-serif;background:#d1d5db;color:#111;padding:16px 0 40px}
    .print-bar{position:fixed;top:0;left:0;right:0;background:#fff;border-bottom:1px solid #ddd;padding:8px 16px;display:flex;justify-content:flex-end;z-index:100}
    .print-bar button{background:#f97316;color:#fff;border:none;border-radius:8px;padding:9px 22px;font-size:14px;font-weight:bold;cursor:pointer}
    .a4{width:794px;min-width:794px;margin:56px auto 0;background:#fff;padding:40px;box-shadow:0 4px 24px rgba(0,0,0,0.18);border-radius:4px}
    .header{background:#f9fafb;padding:24px;border-bottom:1px solid #e5e7eb;margin:-40px -40px 32px -40px;border-radius:4px 4px 0 0;display:flex;justify-content:space-between;align-items:flex-start;gap:16px}
    .logo{max-height:56px;margin-bottom:12px;display:block}
    .emp-nome{font-size:18px;font-weight:bold}
    .emp-info{font-size:12px;color:#6b7280;margin-top:4px;line-height:1.7}
    .num-area{text-align:right;flex-shrink:0}
    .num{font-size:20px;font-weight:bold;color:#f97316;font-family:monospace}
    .grid2{display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:24px}
    .lbl{font-size:10px;text-transform:uppercase;color:#9ca3af;margin-bottom:3px;letter-spacing:.05em}
    .val{font-size:15px;font-weight:600;color:#111}
    table{width:100%;border-collapse:collapse;margin-bottom:24px;font-size:13px}
    th{text-align:left;padding:8px 4px;font-size:11px;color:#6b7280;font-weight:600;border-bottom:2px solid #d1d5db}
    th.r,td.r{text-align:right}
    td{padding:10px 4px;border-bottom:1px solid #f3f4f6;color:#374151}
    td.mono{font-family:monospace}
    td.gray{color:#9ca3af;font-size:11px}
    td.bold{font-weight:600;color:#111}
    .totals{border-top:2px solid #111;padding-top:12px;margin-bottom:24px}
    .tot-row{display:flex;justify-content:space-between;padding:3px 0;font-size:13px;color:#6b7280}
    .tot-final{display:flex;justify-content:space-between;font-size:16px;font-weight:bold;border-top:1px solid #e5e7eb;margin-top:8px;padding-top:8px}
    .orange{color:#f97316}
    .card{background:#f9fafb;border:1px solid #e5e7eb;border-radius:8px;padding:12px 16px;margin-bottom:16px}
    .garantia{background:#fff8f3;border-color:#fed7aa}
    .sig{text-align:center;margin-top:32px;padding-top:20px;border-top:1px solid #e5e7eb}
    @media print{.print-bar{display:none}.a4{box-shadow:none;margin-top:0}}
    @media(max-width:860px){body{padding:0}.a4{margin:48px 0 0}}
  </style>
</head>
<body>
<div class="print-bar"><button onclick="window.print()">&#128438; Imprimir</button></div>
<div class="a4">
  <div class="header">
    <div>
      ${empresa.logo ? `<img src="${empresa.logo}" class="logo" alt="Logo"/>` : ''}
      <div class="emp-nome">${empresa.nome || ''}</div>
      <div class="emp-info">
        ${empresa.cnpj ? `CNPJ: ${empresa.cnpj}<br/>` : ''}
        ${empresa.end ? `${[empresa.end, empresa.num, empresa.cidade, empresa.uf].filter(Boolean).join(', ')}<br/>` : ''}
        ${empresa.tel ? `Tel: ${empresa.tel}<br/>` : ''}
        ${empresa.email ? `${empresa.email}<br/>` : ''}
        ${empresa.site ? empresa.site : ''}
      </div>
    </div>
    <div class="num-area">
      <div style="font-size:11px;color:#9ca3af;text-transform:uppercase">${proposal.tipo?.toUpperCase() || 'ORÇAMENTO'}</div>
      <div class="num">#${proposal.num}</div>
      <div style="font-size:11px;color:#6b7280;margin-top:4px;line-height:1.8">
        Emissão: ${proposal.emissao || '—'}<br/>
        ${proposal.validade ? `Válido até: ${proposal.validade}<br/>` : ''}
        ${proposal.prazo_execucao ? `Prazo: ${proposal.prazo_execucao} dia(s)` : ''}
      </div>
    </div>
  </div>
  <div class="grid2">
    <div><div class="lbl">Destinado a</div><div class="val">${proposal.cliente_nome || '—'}</div>${proposal.cliente_dados?.cpf_cnpj ? `<div style="font-size:12px;color:#6b7280;margin-top:2px">CPF/CNPJ: ${proposal.cliente_dados.cpf_cnpj}</div>` : ''}</div>
    <div><div class="lbl">Título</div><div class="val">${proposal.titulo || ''}</div></div>
  </div>
  ${serviceItems.length > 0 ? `
  <div style="font-size:11px;font-weight:600;color:#6b7280;text-transform:uppercase;border-bottom:1px solid #e5e7eb;padding-bottom:4px;margin-bottom:8px;letter-spacing:.05em">Serviços</div>
  <table><thead><tr><th style="width:44px">Nº</th><th>Descrição</th><th class="r" style="width:46px">Qtd</th><th style="width:36px">Un</th><th class="r" style="width:100px">Unit.</th><th class="r" style="width:100px">Total</th></tr></thead><tbody>
  ${serviceItems.map(it => `<tr><td class="gray">${String(it.position).padStart(2,'0')}</td><td class="bold">${it.descricao}</td><td class="r">${it.quantidade}</td><td class="gray" style="padding-left:8px">${it.unidade}</td><td class="r mono">${brl(it.valor_unitario)}</td><td class="r mono bold">${brl(it.valor_total)}</td></tr>`).join('')}
  </tbody></table>` : ''}
  ${materialItems.length > 0 ? `
  <div style="font-size:11px;font-weight:600;color:#6b7280;text-transform:uppercase;border-bottom:1px solid #e5e7eb;padding-bottom:4px;margin-bottom:8px;letter-spacing:.05em">Materiais</div>
  <table><thead><tr><th style="width:44px">Nº</th><th>Descrição</th><th class="r" style="width:46px">Qtd</th><th style="width:36px">Un</th><th class="r" style="width:100px">Unit.</th><th class="r" style="width:100px">Total</th></tr></thead><tbody>
  ${materialItems.map(it => `<tr><td class="gray">${String(it.position).padStart(2,'0')}</td><td class="bold">${it.descricao}</td><td class="r">${it.quantidade}</td><td class="gray" style="padding-left:8px">${it.unidade}</td><td class="r mono">${brl(it.valor_unitario)}</td><td class="r mono bold">${brl(it.valor_total)}</td></tr>`).join('')}
  </tbody></table>` : ''}
  <div class="totals">
    ${totalServicos > 0 ? `<div class="tot-row"><span>Valor total dos serviços</span><span class="mono">${brl(totalServicos)}</span></div>` : ''}
    ${totalMateriais > 0 ? `<div class="tot-row"><span>Valor total dos materiais</span><span class="mono">${brl(totalMateriais)}</span></div>` : ''}
    ${proposal.desconto > 0 ? `<div class="tot-row"><span>Subtotal</span><span class="mono">${brl(subtotal)}</span></div><div class="tot-row"><span>Desconto</span><span class="mono" style="color:#dc2626">- ${brl(proposal.desconto)}</span></div>` : ''}
    <div class="tot-final"><span>TOTAL</span><span class="mono orange">${brl(proposal.valor)}</span></div>
  </div>
  ${fp ? `<div class="card"><div class="lbl">Forma de Pagamento</div><div style="font-weight:600;font-size:14px;margin-top:2px">${PAGAMENTO_LABELS[fp.tipo] || fp.tipo}</div>${fp.tipo === 'cartao_parcelado' && fp.parcelas ? `<div style="font-size:13px;color:#6b7280;margin-top:4px">${fp.parcelas}x de ${brl(fp.valor_parcela)}</div>` : ''}${fp.tipo === 'pix' && empresa.chave_pix ? `<div style="margin-top:8px;font-size:13px">Chave PIX: <strong class="orange">${empresa.chave_pix}</strong></div>` : ''}</div>` : ''}
  ${proposal.obs ? `<div class="card"><div class="lbl">Observações</div><div style="margin-top:6px;font-size:13px;color:#374151;white-space:pre-line">${proposal.obs}</div></div>` : ''}
  ${(proposal.garantia_prazo || proposal.garantia_condicoes) ? `<div class="card garantia"><div class="lbl">Garantia dos Serviços</div>${proposal.garantia_prazo ? `<div style="font-weight:600;margin-top:6px">Prazo: ${proposal.garantia_prazo}</div>` : ''}${proposal.garantia_condicoes ? `<div style="font-size:13px;color:#6b7280;margin-top:4px;white-space:pre-line">${proposal.garantia_condicoes}</div>` : ''}</div>` : ''}
  ${empresa.assinatura_empresa ? `<div class="sig"><div style="font-size:11px;color:#9ca3af;margin-bottom:8px">ASSINATURA DO PRESTADOR</div><img src="${empresa.assinatura_empresa}" style="max-height:60px;display:block;margin:0 auto;border-bottom:1px solid #9ca3af;padding-bottom:4px;padding-left:16px;padding-right:16px" alt="Assinatura"/><div style="font-size:12px;color:#6b7280;margin-top:4px">${empresa.nome || ''}</div></div>` : ''}
</div>
</body>
</html>`;
    win.document.write(html);
    win.document.close();
  };

  return (
    <div style={{ minHeight: '100vh', background: '#d1d5db', paddingTop: '24px', paddingBottom: '32px' }}>
      <Toaster position="top-right" theme="light" richColors />

      {/* Barra utilitária — fora do A4 */}
      <div style={{ maxWidth: '860px', margin: '0 auto', padding: '0 16px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          {isLoggedIn && (
            <button data-testid="voltar-sistema-btn" onClick={() => navigate(`/proposals/${id}`)}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#374151', background: '#fff', border: '1px solid #d1d5db', padding: '6px 12px', borderRadius: '8px', cursor: 'pointer' }}>
              <ChevronLeft size={14} />
              Voltar ao Sistema
            </button>
          )}
        </div>
        <button onClick={openPrintPage} data-testid="public-print-btn"
          style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#374151', background: '#fff', border: '1px solid #d1d5db', padding: '6px 12px', borderRadius: '8px', cursor: 'pointer' }}>
          <Printer size={14} />
          Imprimir
        </button>
      </div>

      {/* Wrapper A4 — zoom automático para encaixar na tela, pinch-zoom disponível */}
      <div style={{ width: '100%' }}>
        <div
          style={{
            width: '794px',
            zoom: docScale,
            margin: '0 auto',
            background: '#ffffff',
            color: '#111',
            boxShadow: '0 4px 24px rgba(0,0,0,0.2)',
            borderRadius: '4px',
          }}
        >
          {/* Cabeçalho */}
          <div style={{ background: '#f9fafb', padding: '32px 40px 24px', borderBottom: '1px solid #e5e7eb', borderRadius: '4px 4px 0 0' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px' }}>
              <div style={{ flex: 1 }}>
                {empresa.logo && <img src={empresa.logo} alt="Logo" style={{ height: '56px', marginBottom: '12px', objectFit: 'contain', display: 'block' }} />}
                <div style={{ fontWeight: 'bold', fontSize: '18px', color: '#111' }}>{empresa.nome || 'Empresa'}</div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px', lineHeight: '1.7' }}>
                  {empresa.cnpj && <div>CNPJ: {empresa.cnpj}</div>}
                  {(empresa.end || empresa.cidade) && <div>{[empresa.end, empresa.num, empresa.cidade, empresa.uf].filter(Boolean).join(', ')}</div>}
                  {empresa.tel && <div>Tel: {empresa.tel}</div>}
                  {empresa.email && <div>{empresa.email}</div>}
                  {empresa.site && <div>{empresa.site}</div>}
                </div>
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{ fontSize: '11px', color: '#9ca3af', textTransform: 'uppercase' }}>{proposal.tipo?.toUpperCase() || 'ORÇAMENTO'}</div>
                <div style={{ fontFamily: 'monospace', fontWeight: 'bold', color: '#f97316', fontSize: '20px' }}>#{proposal.num}</div>
                <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px', lineHeight: '1.8' }}>
                  Emissão: {proposal.emissao || '—'}<br/>
                  {proposal.validade && <>Válido até: {proposal.validade}<br/></>}
                  {proposal.prazo_execucao && <>Prazo: {proposal.prazo_execucao} dia(s)</>}
                </div>
              </div>
            </div>
          </div>

          <div style={{ padding: '32px 40px' }}>
            {/* Cliente + Título */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '28px' }}>
              <div>
                <div style={{ fontSize: '10px', textTransform: 'uppercase', color: '#9ca3af', marginBottom: '3px', letterSpacing: '.05em' }}>Destinado a</div>
                <div style={{ fontWeight: '600', fontSize: '15px', color: '#111' }}>{proposal.cliente_nome || '—'}</div>
                {proposal.cliente_dados?.cpf_cnpj && <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>CPF/CNPJ: {proposal.cliente_dados.cpf_cnpj}</div>}
              </div>
              <div>
                <div style={{ fontSize: '10px', textTransform: 'uppercase', color: '#9ca3af', marginBottom: '3px', letterSpacing: '.05em' }}>Título</div>
                <div style={{ fontWeight: '600', fontSize: '15px', color: '#111' }}>{proposal.titulo}</div>
              </div>
            </div>

            {/* Serviços */}
            {serviceItems.length > 0 && (
              <div style={{ marginBottom: '8px' }}>
                <div style={{ fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '.05em', borderBottom: '1px solid #e5e7eb', paddingBottom: '4px', marginBottom: '8px' }}>Serviços</div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', marginBottom: '20px' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #d1d5db' }}>
                      <th style={{ textAlign: 'left', padding: '8px 4px', fontSize: '11px', color: '#6b7280', fontWeight: 600, width: '44px' }}>Nº</th>
                      <th style={{ textAlign: 'left', padding: '8px 4px', fontSize: '11px', color: '#6b7280', fontWeight: 600 }}>Descrição</th>
                      <th style={{ textAlign: 'right', padding: '8px 4px', fontSize: '11px', color: '#6b7280', fontWeight: 600, width: '46px' }}>Qtd</th>
                      <th style={{ textAlign: 'left', padding: '8px 4px 8px 8px', fontSize: '11px', color: '#6b7280', fontWeight: 600, width: '36px' }}>Un</th>
                      <th style={{ textAlign: 'right', padding: '8px 4px', fontSize: '11px', color: '#6b7280', fontWeight: 600, width: '100px' }}>Unit.</th>
                      <th style={{ textAlign: 'right', padding: '8px 4px', fontSize: '11px', color: '#6b7280', fontWeight: 600, width: '100px' }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {serviceItems.map(item => (
                      <tr key={item.position} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={{ padding: '10px 4px', fontFamily: 'monospace', fontSize: '11px', color: '#9ca3af' }}>{String(item.position).padStart(2, '0')}</td>
                        <td style={{ padding: '10px 4px', color: '#111', fontWeight: 500 }}>{item.descricao}</td>
                        <td style={{ padding: '10px 4px', textAlign: 'right', color: '#374151' }}>{item.quantidade}</td>
                        <td style={{ padding: '10px 4px 10px 8px', fontSize: '11px', color: '#6b7280' }}>{item.unidade}</td>
                        <td style={{ padding: '10px 4px', textAlign: 'right', fontFamily: 'monospace', color: '#374151' }}>{brl(item.valor_unitario)}</td>
                        <td style={{ padding: '10px 4px', textAlign: 'right', fontFamily: 'monospace', color: '#111', fontWeight: 600 }}>{brl(item.valor_total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Materiais */}
            {materialItems.length > 0 && (
              <div style={{ marginBottom: '8px' }}>
                <div style={{ fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '.05em', borderBottom: '1px solid #e5e7eb', paddingBottom: '4px', marginBottom: '8px' }}>Materiais</div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', marginBottom: '20px' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #d1d5db' }}>
                      <th style={{ textAlign: 'left', padding: '8px 4px', fontSize: '11px', color: '#6b7280', fontWeight: 600, width: '44px' }}>Nº</th>
                      <th style={{ textAlign: 'left', padding: '8px 4px', fontSize: '11px', color: '#6b7280', fontWeight: 600 }}>Descrição</th>
                      <th style={{ textAlign: 'right', padding: '8px 4px', fontSize: '11px', color: '#6b7280', fontWeight: 600, width: '46px' }}>Qtd</th>
                      <th style={{ textAlign: 'left', padding: '8px 4px 8px 8px', fontSize: '11px', color: '#6b7280', fontWeight: 600, width: '36px' }}>Un</th>
                      <th style={{ textAlign: 'right', padding: '8px 4px', fontSize: '11px', color: '#6b7280', fontWeight: 600, width: '100px' }}>Unit.</th>
                      <th style={{ textAlign: 'right', padding: '8px 4px', fontSize: '11px', color: '#6b7280', fontWeight: 600, width: '100px' }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {materialItems.map(item => (
                      <tr key={item.position} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={{ padding: '10px 4px', fontFamily: 'monospace', fontSize: '11px', color: '#9ca3af' }}>{String(item.position).padStart(2, '0')}</td>
                        <td style={{ padding: '10px 4px', color: '#111', fontWeight: 500 }}>{item.descricao}</td>
                        <td style={{ padding: '10px 4px', textAlign: 'right', color: '#374151' }}>{item.quantidade}</td>
                        <td style={{ padding: '10px 4px 10px 8px', fontSize: '11px', color: '#6b7280' }}>{item.unidade}</td>
                        <td style={{ padding: '10px 4px', textAlign: 'right', fontFamily: 'monospace', color: '#374151' }}>{brl(item.valor_unitario)}</td>
                        <td style={{ padding: '10px 4px', textAlign: 'right', fontFamily: 'monospace', color: '#111', fontWeight: 600 }}>{brl(item.valor_total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Totais */}
            <div style={{ borderTop: '2px solid #111', paddingTop: '12px', marginBottom: '24px' }}>
              {totalServicos > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#6b7280', padding: '3px 0' }}><span>Valor total dos serviços</span><span style={{ fontFamily: 'monospace' }}>{brl(totalServicos)}</span></div>}
              {totalMateriais > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#6b7280', padding: '3px 0' }}><span>Valor total dos materiais</span><span style={{ fontFamily: 'monospace' }}>{brl(totalMateriais)}</span></div>}
              {proposal.desconto > 0 && (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#6b7280', padding: '3px 0' }}><span>Subtotal</span><span style={{ fontFamily: 'monospace' }}>{brl(subtotal)}</span></div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#6b7280', padding: '3px 0' }}><span>Desconto</span><span style={{ fontFamily: 'monospace', color: '#dc2626' }}>- {brl(proposal.desconto)}</span></div>
                </>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', fontSize: '16px', color: '#111', paddingTop: '8px', borderTop: '1px solid #e5e7eb', marginTop: '8px' }}>
                <span>TOTAL</span>
                <span style={{ fontFamily: 'monospace', color: '#f97316' }}>{brl(proposal.valor)}</span>
              </div>
            </div>

            {/* Pagamento */}
            {fp && (
              <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px' }}>
                <div style={{ fontSize: '10px', textTransform: 'uppercase', color: '#9ca3af', marginBottom: '4px', letterSpacing: '.05em' }}>Forma de Pagamento</div>
                <div style={{ fontWeight: 600, fontSize: '14px', color: '#111' }}>{PAGAMENTO_LABELS[fp.tipo] || fp.tipo}</div>
                {fp.tipo === 'cartao_parcelado' && fp.parcelas && <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>{fp.parcelas}x de <span style={{ color: '#f97316', fontFamily: 'monospace' }}>{brl(fp.valor_parcela)}</span></div>}
                {fp.tipo === 'boleto' && fp.parcelas > 1 && <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>{fp.parcelas}x de <span style={{ color: '#f97316', fontFamily: 'monospace' }}>{brl(fp.valor_parcela)}</span></div>}
                {fp.tipo === 'boleto' && fp.vencimento_boleto && <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>Vencimento: {fp.vencimento_boleto}</div>}
                {fp.tipo === 'pix' && empresa.chave_pix && (
                  <div style={{ marginTop: '12px', background: '#f3f4f6', borderRadius: '8px', padding: '12px' }}>
                    <div style={{ fontSize: '10px', textTransform: 'uppercase', color: '#9ca3af', marginBottom: '6px', letterSpacing: '.05em' }}>Chave PIX</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '13px', fontFamily: 'monospace', color: '#f97316', flex: 1, wordBreak: 'break-all' }}>{empresa.chave_pix}</span>
                      <button
                        data-testid="copy-pix-btn"
                        onClick={() => copyPix(empresa.chave_pix)}
                        style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: 600, border: 'none', cursor: 'pointer', background: pixCopied ? '#dcfce7' : '#f97316', color: pixCopied ? '#15803d' : '#fff' }}
                      >
                        {pixCopied ? <><CheckCheck size={13} /> Copiado!</> : <><Copy size={13} /> Copiar</>}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Observações */}
            {proposal.obs && (
              <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px' }}>
                <div style={{ fontSize: '10px', textTransform: 'uppercase', color: '#9ca3af', marginBottom: '4px', letterSpacing: '.05em' }}>Observações</div>
                <p style={{ fontSize: '13px', color: '#374151', whiteSpace: 'pre-line' }}>{proposal.obs}</p>
              </div>
            )}

            {/* Garantia */}
            {(proposal.garantia_prazo || proposal.garantia_condicoes) && (
              <div style={{ background: '#fff8f3', border: '1px solid #fed7aa', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                  <Shield size={13} color="#f97316" />
                  <div style={{ fontSize: '10px', textTransform: 'uppercase', color: '#9ca3af', letterSpacing: '.05em' }}>Garantia dos Serviços</div>
                </div>
                {proposal.garantia_prazo && <div style={{ fontWeight: 600, fontSize: '14px', color: '#111', marginBottom: '4px' }}>Prazo: {proposal.garantia_prazo}</div>}
                {proposal.garantia_condicoes && <p style={{ fontSize: '13px', color: '#6b7280', whiteSpace: 'pre-line' }}>{proposal.garantia_condicoes}</p>}
              </div>
            )}

            {/* Assinatura */}
            {empresa.assinatura_empresa && (
              <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '24px', textAlign: 'center' }}>
                <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '8px' }}>ASSINATURA DO PRESTADOR</div>
                <img src={empresa.assinatura_empresa} alt="Assinatura" style={{ maxHeight: '60px', display: 'block', margin: '0 auto', borderBottom: '1px solid #9ca3af', paddingBottom: '4px', paddingLeft: '16px', paddingRight: '16px' }} />
                <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>{empresa.nome}</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Seções fora do A4 */}
      <div style={{ maxWidth: '860px', margin: '16px auto 0', padding: '0 16px' }}>

        {/* Termos e Condições */}
        <div data-testid="terms-section" className="bg-white border border-gray-200 rounded-2xl p-5 space-y-4 shadow-sm mb-4">
          <div className="flex items-center gap-2">
            <ScrollText size={18} className="text-yellow-500" />
            <h2 className="font-heading font-semibold text-gray-900">Termos e Condições</h2>
          </div>
          <p className="text-sm text-gray-600">Antes de aprovar este orçamento, leia os Termos e Condições.</p>
          <button data-testid="view-terms-btn" onClick={() => setShowTermsOverlay(true)}
            className="w-full flex items-center justify-center gap-2 bg-yellow-400 hover:bg-yellow-300 text-black font-bold py-3 rounded-xl transition-all shadow-md shadow-yellow-400/20 text-sm">
            <ScrollText size={16} />
            Visualizar Termos e Condições
          </button>
          <label data-testid="terms-checkbox-label"
            className={`flex items-start gap-3 cursor-pointer p-3 rounded-xl border transition-all ${termsAccepted ? 'border-yellow-400/50 bg-yellow-50' : termsError ? 'border-red-400 bg-red-50' : 'border-gray-300 hover:border-gray-400'}`}>
            <div className="relative mt-0.5 flex-shrink-0">
              <input type="checkbox" data-testid="terms-checkbox" checked={termsAccepted}
                onChange={e => { setTermsAccepted(e.target.checked); if (e.target.checked) setTermsError(false); }}
                className="sr-only" />
              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${termsAccepted ? 'bg-yellow-400 border-yellow-400' : termsError ? 'border-red-400' : 'border-gray-400'}`}>
                {termsAccepted && <Check size={12} className="text-black" strokeWidth={3} />}
              </div>
            </div>
            <span className="text-sm text-gray-700 leading-snug">Li e concordo com todos os <span className="text-yellow-600 font-medium">Termos e Condições</span> deste orçamento.</span>
          </label>
          {termsError && (
            <p data-testid="terms-error-msg" className="text-red-500 text-sm font-medium flex items-center gap-2">
              <X size={14} />É preciso ler e aceitar o Termo e Condições.
            </p>
          )}
        </div>

        {/* Botões de ação */}
        {canAct && (
          <div className="grid grid-cols-2 gap-3 mb-4">
            <button onClick={() => act('reject')} disabled={rejecting} data-testid="reject-btn"
              className="flex items-center justify-center gap-2 border-2 border-red-300 bg-red-50 text-red-600 hover:bg-red-100 font-semibold py-3 rounded-xl transition-colors">
              <X size={18} />{rejecting ? 'Processando...' : 'Recusar'}
            </button>
            <button onClick={() => act('approve')} disabled={approving} data-testid="approve-btn"
              className="flex items-center justify-center gap-2 border-2 border-green-300 bg-green-50 text-green-700 hover:bg-green-100 font-semibold py-3 rounded-xl transition-colors">
              <Check size={18} />{approving ? 'Processando...' : 'Aprovar'}
            </button>
          </div>
        )}

        {/* Status após ação */}
        {proposal.status === 'aprovado' && (
          <div className="bg-green-50 border border-green-300 rounded-xl p-4 text-center mb-4">
            <Check size={20} className="text-green-600 mx-auto mb-1" />
            <p className="text-green-700 font-semibold">Orçamento aprovado!</p>
            <p className="text-green-600 text-sm mt-1">Use o botão "Imprimir" acima para salvar ou imprimir o orçamento.</p>
          </div>
        )}
        {proposal.status === 'recusado' && (
          <div className="bg-red-50 border border-red-300 rounded-xl p-4 text-center mb-4">
            <X size={20} className="text-red-500 mx-auto mb-1" />
            <p className="text-red-600 font-medium">Orçamento recusado</p>
          </div>
        )}

        {/* Fotos da Execução */}
        {photos.length > 0 && (
          <div data-testid="execution-photos-section" className="bg-white border border-gray-200 rounded-2xl p-5 space-y-4 shadow-sm">
            <h2 className="font-heading font-semibold text-gray-900 flex items-center gap-2">
              <Image size={18} className="text-orange-500" />
              Fotos da Execução
              <span className="text-xs text-gray-400 font-normal">({photos.length})</span>
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {photos.map((photo, idx) => (
                <button key={idx} data-testid={`execution-photo-thumb-${idx}`} onClick={() => openLightbox(idx)}
                  className="relative group aspect-square rounded-xl overflow-hidden bg-gray-100 border border-gray-200 hover:border-orange-400 transition-all focus:outline-none">
                  <img src={photo} alt={`Foto de execução ${idx + 1}`} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <ZoomIn size={22} className="text-white" />
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Lightbox — fundo escuro (melhor para fotos) */}
      {lightboxIndex !== null && (
        <div data-testid="lightbox-overlay" className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center" onClick={closeLightbox}>
          <button data-testid="lightbox-close-btn" onClick={closeLightbox} className="absolute top-4 right-4 z-10 w-10 h-10 bg-zinc-800 hover:bg-zinc-700 rounded-full flex items-center justify-center transition-colors">
            <X size={20} className="text-white" />
          </button>
          <div className="absolute top-4 left-1/2 -translate-x-1/2 text-sm text-zinc-400 bg-zinc-800/80 px-3 py-1 rounded-full">{lightboxIndex + 1} / {photos.length}</div>
          {photos.length > 1 && (
            <button data-testid="lightbox-prev-btn" onClick={(e) => { e.stopPropagation(); prevPhoto(); }} className="absolute left-3 sm:left-6 z-10 w-10 h-10 bg-zinc-800 hover:bg-zinc-700 rounded-full flex items-center justify-center transition-colors">
              <ChevronLeft size={22} className="text-white" />
            </button>
          )}
          <img data-testid="lightbox-image" src={photos[lightboxIndex]} alt={`Foto ${lightboxIndex + 1}`} className="max-w-[90vw] max-h-[85vh] object-contain rounded-xl shadow-2xl" onClick={(e) => e.stopPropagation()} />
          {photos.length > 1 && (
            <button data-testid="lightbox-next-btn" onClick={(e) => { e.stopPropagation(); nextPhoto(); }} className="absolute right-3 sm:right-6 z-10 w-10 h-10 bg-zinc-800 hover:bg-zinc-700 rounded-full flex items-center justify-center transition-colors">
              <ChevronRight size={22} className="text-white" />
            </button>
          )}
        </div>
      )}

      {/* Overlay de Termos */}
      {showTermsOverlay && (
        <div data-testid="terms-overlay" className="fixed inset-0 z-50 bg-white flex flex-col">
          <div className="flex items-center gap-3 px-4 py-4 border-b border-gray-200 bg-gray-50 flex-shrink-0">
            <button data-testid="terms-back-btn" onClick={() => setShowTermsOverlay(false)}
              className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 border border-gray-300 hover:border-gray-400 px-3 py-1.5 rounded-lg transition-colors">
              <ArrowLeft size={14} />
              Voltar ao Orçamento
            </button>
            <h2 className="font-heading font-semibold text-gray-900 text-sm flex-1 text-center pr-16">Termos e Condições</h2>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-6">
            <div className="max-w-2xl mx-auto">
              <pre data-testid="terms-text" className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed font-sans">{termsText}</pre>
              <div className="mt-8 pt-6 border-t border-gray-200">
                <label className={`flex items-start gap-3 cursor-pointer p-4 rounded-xl border transition-all ${termsAccepted ? 'border-yellow-400/50 bg-yellow-50' : 'border-gray-300 hover:border-gray-400'}`}>
                  <div className="relative mt-0.5 flex-shrink-0">
                    <input type="checkbox" checked={termsAccepted} onChange={e => { setTermsAccepted(e.target.checked); if (e.target.checked) setTermsError(false); }} className="sr-only" />
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${termsAccepted ? 'bg-yellow-400 border-yellow-400' : 'border-gray-400'}`}>
                      {termsAccepted && <Check size={12} className="text-black" strokeWidth={3} />}
                    </div>
                  </div>
                  <span className="text-sm text-gray-700 leading-snug">Li e concordo com todos os <span className="text-yellow-600 font-medium">Termos e Condições</span> deste orçamento.</span>
                </label>
                <button onClick={() => setShowTermsOverlay(false)} className="w-full mt-4 bg-yellow-400 hover:bg-yellow-300 text-black font-bold py-3 rounded-xl transition-all shadow-md shadow-yellow-400/20 text-sm">
                  {termsAccepted ? 'Confirmar e Voltar ao Orçamento' : 'Voltar ao Orçamento'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
